import { useState } from 'react'
import { tenantLogoUrl } from '@/lib/tenantLogoUrl'

type TenantMarkProps = {
  /** Tenant's marketing domain (drives the logo.dev URL). `null` for opt-out. */
  logoDomain: string | null
  /** Single uppercase letter for the fallback tile. */
  initial: string
  /** Workspace name — the image's accessible name. */
  name: string
}

/**
 * Layered tenant mark.
 *
 * A graphite letter tile with the logo.dev image stacked over it. If the
 * image loads, `data-loaded="true"` flips the tile background to white and
 * CSS fades the letter out. If the image errors, or the tenant has no
 * `logoDomain`, the letter stays — so a blocked CDN degrades to a readable
 * tile rather than an empty square.
 *
 * The state lives on a `data-` attribute rather than `:has()` because
 * enterprise browsers pinned behind corporate upgrade cadences are a large
 * share of this app's traffic.
 */
export function TenantMark({ logoDomain, initial, name }: TenantMarkProps) {
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(false)
  const url = tenantLogoUrl(logoDomain)
  const showImg = !!url && !failed

  return (
    <span
      className="wsrow__mark"
      data-loaded={loaded ? 'true' : 'false'}
      aria-hidden="true"
    >
      <span className="wsrow__mark-letter">{initial}</span>
      {showImg ? (
        <img
          className={
            'wsrow__mark-img' + (loaded ? ' wsrow__mark-img--loaded' : '')
          }
          src={url}
          alt={name}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
        />
      ) : null}
    </span>
  )
}
